import { Reveal, Overline } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { useLang } from "../i18n";

const KAZUKI_LONG = [
  "こんにちは。Hello, I'm Kazuki Kawamura.",
  "I am a Japanese-English translator, interpreter, and cultural liaison dedicated to helping people connect across languages, cultures, and industries.",
  "Throughout my career, I have supported communication between Japanese expatriates and Canadian engineering teams within manufacturing environments, helping bridge not only language barriers but also the cultural differences that often influence how people work, collaborate, and build relationships.",
  "My passion for cross-cultural communication was shaped from an early age. Growing up in a family involved in international business, education, journalism, and language instruction, I was constantly exposed to the value of clear communication and global perspectives. These experiences inspired me to pursue a path that helps people navigate unfamiliar environments with greater confidence and understanding.",
  "Today, I combine professional experience with a practical, people-focused approach to help individuals and organizations succeed in international settings.",
];

const KAZUKI_SHORT = "Kazuki Kawamura is a Japanese-English translator, interpreter, and cultural liaison specializing in communication between Japanese expatriates and Canadian engineering teams. Drawing on a background influenced by international business, education, journalism, and language instruction, he founded Kakehashi to help individuals successfully navigate life, work, and cultural transitions between Japan and Canada.";

const KAHO_BIO = "Kaho came to Canada after completing her studies in Japan and has built her life here over the past decade, navigating the permanent residency process independently — the paperwork, the professionals, the timelines nobody warns you about. That firsthand experience shapes how she supports clients making the same transition: practical, thorough, and attentive to the details that matter when you're doing something unfamiliar in a second language.";

const FOUNDER_CARD = (initials) => (
  <div className="bg-navy rounded-sm p-10 h-full flex flex-col justify-between min-h-[18rem]">
    <span className="font-serif text-6xl text-cream-surface/15 leading-none">{initials}</span>
    <span className="font-mono text-[10px] tracking-[0.25em] text-coral-light uppercase">Kakehashi · 架け橋</span>
  </div>
);

export default function About() {
  const { t } = useLang();
  const chapters = [
    { n: "第一章", t: t("about.ch1t"), body: t("about.ch1") },
    { n: "第二章", t: t("about.ch2t"), body: t("about.ch2") },
    { n: "第三章", t: t("about.ch3t"), body: t("about.ch3") },
  ];
  return (
    <div data-testid="about-page" className="bg-cream pt-16">
      <section className="bg-dawn">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-20 sm:pt-28 pb-16">
        <Reveal>
          <Overline>{t("about.overline")}</Overline>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight text-navy max-w-3xl">
            {t("about.title")}
          </h1>
          <p className="mt-8 text-base sm:text-lg leading-relaxed text-charcoal/75 max-w-2xl">
            {t("about.intro")}
          </p>
          <p data-testid="about-company-line" className="mt-6 text-base sm:text-lg leading-relaxed text-charcoal/75 max-w-2xl border-l-2 border-gold pl-6">
            Ruten Borealis is a Japan–Canada consultancy founded by Kazuki Kawamura and Kaho Nishimura — one
            bridging language and business in professional and manufacturing settings, the other having navigated
            the move to Canada firsthand. Together they cover both sides of the crossing: the organizations sending
            people, and the people arriving. Kakehashi (架け橋) is the platform through which that work happens.
          </p>
        </Reveal>
        </div>
      </section>

      {/* Founders */}
      <section data-testid="founders-section" className="border-y border-sand bg-cream-surface">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-28 space-y-24">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">{FOUNDER_CARD("KK")}</div>
              <div className="lg:col-span-8">
                <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-coral-dark">Founder — Translator, Interpreter & Cultural Liaison</p>
                <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-navy">Kazuki Kawamura</h2>
                <div className="mt-6 space-y-5 text-base leading-relaxed text-charcoal/75 max-w-2xl">
                  {KAZUKI_LONG.map((p, i) => (
                    <p key={i} className={i === 0 ? "font-serif text-xl text-navy" : ""}>{p}</p>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-8 lg:order-1 order-2">
                <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-coral-dark">Co-Founder — Relocation & Settlement Support</p>
                <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-navy">Kaho Nishimura</h2>
                <p className="mt-6 text-base leading-relaxed text-charcoal/75 max-w-2xl">{KAHO_BIO}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {["Relocation support", "Settlement guidance", "Working holiday preparation"].map((tag) => (
                    <span key={tag} className="text-[11px] font-mono tracking-wide border border-sand text-charcoal/70 px-2.5 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-4 lg:order-2 order-1">
                <div className="overflow-hidden rounded-sm border border-sand h-full min-h-[18rem]">
                  <img
                    src="/images/passport-tea-maple.jpg"
                    alt="A leather passport cover, a cup of tea, and a maple leaf on marble"
                    data-testid="kaho-photo"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why I created Kakehashi + My philosophy */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <div data-testid="why-created-section">
              <Overline>WHY WE BUILT KAKEHASHI</Overline>
              <p className="mt-6 text-base sm:text-lg leading-relaxed text-charcoal/80">
                'Kakehashi' (架け橋) means bridge in Japanese. We named our platform for it because that's exactly
                what we set out to build: a way to make the journey between Japan and Canada easier, clearer, and
                more accessible for everyone. Whether you are
                preparing for a Working Holiday, moving to Canada for the first time, supporting Japanese employees
                abroad, looking to better understand cultural differences, or seeking practical advice based on real
                experiences — my goal is to provide reliable information, helpful resources, and guidance that people can trust.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="philosophy-section">
              <Overline>MY PHILOSOPHY</Overline>
              <p className="mt-6 text-base sm:text-lg leading-relaxed text-charcoal/80">
                I believe successful communication is about more than words. It is about understanding perspectives,
                respecting differences, and building meaningful connections between people. Every person carries a
                unique story, and every cultural exchange is an opportunity to learn and grow. Through Kakehashi, we
                hope to help create those connections and support others on their own international journey.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Closing line */}
      <section data-testid="closing-line" className="bg-navy">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24">
          <Reveal>
            <p className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-snug tracking-tight text-cream-surface max-w-4xl">
              Connecting People<span className="text-coral-light">.</span> Connecting Cultures<span className="text-coral-light">.</span>{" "}
              Connecting Opportunities<span className="text-coral-light">.</span>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-sand bg-cream-surface">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-28 space-y-20">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={0.05}>
              <div data-testid={`manifesto-chapter-${i + 1}`} className="grid grid-cols-1 md:grid-cols-12 gap-8">
                <div className="md:col-span-3">
                  <span className="font-mono text-xs tracking-[0.25em] text-coral-dark">{c.n}</span>
                  <span className="block mt-2 font-mono text-xs text-charcoal/40">0{i + 1}</span>
                </div>
                <div className="md:col-span-9">
                  <h2 className="font-serif text-2xl sm:text-3xl text-navy">{c.t}</h2>
                  <p className="mt-5 text-base leading-relaxed text-charcoal/75 max-w-2xl">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-28">
        <Reveal>
          <Overline>{t("about.offices")}</Overline>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10">
          {[
            {
              city: "Tokyo", img: "https://images.pexels.com/photos/15829771/pexels-photo-15829771.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
              alt: "Traditional Japanese architecture in Tokyo", testId: "about-office-tokyo",
              body: "Our Tokyo practice serves Japanese companies expanding into Canada and Canadian firms operating in Japan — interpretation, advisory, and on-site support from Shibuya.",
            },
            {
              city: "Toronto", img: "https://images.pexels.com/photos/20028518/pexels-photo-20028518.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
              alt: "Toronto street", testId: "about-office-toronto",
              body: "From Yonge Street, our Toronto team works across Ontario's manufacturing corridor and with Canada's Japan-bound exporters, educators, and institutions.",
            },
          ].map((o, i) => (
            <Reveal key={o.city} delay={i * 0.1}>
              <div data-testid={o.testId}>
                <div className="overflow-hidden rounded-sm border border-sand">
                  <img src={o.img} alt={o.alt} className="w-full h-72 object-cover transition-transform duration-700 hover:scale-[1.03]" />
                </div>
                <h3 className="mt-6 font-serif text-2xl text-navy">{o.city}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/70 max-w-md">{o.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Professional Network */}
      <section data-testid="professional-network" className="border-t border-sand bg-cream-surface">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-28">
          <Reveal>
            <Overline>PROFESSIONAL NETWORK</Overline>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-navy max-w-2xl">
              The people who carry the work.
            </h2>
            <p className="mt-5 text-base text-charcoal/70 max-w-2xl">
              A small, trusted network of translators, interpreters, and instructors — every engagement staffed by professionals we know personally.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Kazuki Kawamura", role: "Founder · Translator, Interpreter & Cultural Liaison", bio: KAZUKI_SHORT, tags: ["Technical translation", "Manufacturing liaison", "Business Japanese"], founder: true },
              { name: "Kaho Nishimura", role: "Co-Founder · Relocation & Settlement Support", bio: KAHO_BIO, tags: ["Relocation support", "Settlement guidance", "Working holiday preparation"], founder: true },
              { name: "Mika Tanaka", role: "Senior Simultaneous Interpreter", bio: "Fifteen years in conference booths across Tokyo and Toronto — summits, board meetings, and live proceedings.", tags: ["Simultaneous interpretation", "Board-level EN⇄JP"] },
              { name: "Daniel Suzuki", role: "Technical Translator", bio: "Engineering drawings, specifications, and manuals translated by someone who has read them on a factory floor.", tags: ["Technical translation", "Manufacturing"] },
              { name: "Aya Kobayashi", role: "Language Instructor", bio: "One-to-one instruction for executives and families — business Japanese and practical English, built around your calendar.", tags: ["Business Japanese", "1-to-1 instruction"] },
              { name: "James Whitfield", role: "Language Instructor", bio: "Workplace English coaching for Japanese professionals settling into Canadian offices and plant floors.", tags: ["Workplace English", "Onboarding"] },
            ].map((p, i) => (
              <Reveal key={p.name} delay={i * 0.05}>
                <div data-testid={`network-card-${i}`} className={`rounded-sm p-7 h-full flex flex-col border ${p.founder ? "bg-navy border-navy text-cream-surface" : "bg-cream border-sand"}`}>
                  <p className={`font-serif text-xl ${p.founder ? "text-cream-surface" : "text-navy"}`}>{p.name}</p>
                  <p className={`mt-1.5 font-mono text-[10px] tracking-[0.18em] uppercase ${p.founder ? "text-coral-light" : "text-coral-dark"}`}>{p.role}</p>
                  <p className={`mt-4 text-sm leading-relaxed flex-1 ${p.founder ? "text-[#B7C6E2]" : "text-charcoal/70"}`}>{p.bio}</p>
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {p.tags.map((tag) => (
                      <span key={tag} className={`text-[10px] font-mono tracking-wide px-2 py-1 rounded-full border ${p.founder ? "border-navy-border text-[#B7C6E2]" : "border-sand text-charcoal/60"}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.08}>
            <div className="mt-16 border-t border-sand pt-10">
              <h3 className="font-serif text-2xl text-navy">Specialist Collaborators</h3>
              <p className="mt-3 text-sm text-charcoal/60 max-w-xl">
                Industry specialists and licensed professionals we call in when the work demands them.
              </p>
            </div>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { name: "Sarah Lalonde", role: "Regulated Immigration Consultant (RCIC)", bio: "Licensed advice on visas and settlement matters — brought in whenever a question needs a regulated professional.", tags: ["Immigration referrals", "Settlement"] },
              { name: "Hiroshi Mori", role: "Manufacturing Liaison", bio: "Thirty years across automotive plants in Aichi and Ontario — kaizen vocabulary and shop-floor protocol, carried correctly.", tags: ["Kaizen", "Plant launches"] },
            ].map((p, i) => (
              <Reveal key={p.name} delay={i * 0.06}>
                <div data-testid={`collaborator-card-${i}`} className="bg-cream border border-sand rounded-sm p-7 h-full flex flex-col">
                  <p className="font-serif text-xl text-navy">{p.name}</p>
                  <p className="mt-1.5 font-mono text-[10px] tracking-[0.18em] uppercase text-coral-dark">{p.role}</p>
                  <p className="mt-4 text-sm leading-relaxed text-charcoal/70 flex-1">{p.bio}</p>
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {p.tags.map((tag) => (
                      <span key={tag} className="text-[10px] font-mono tracking-wide border border-sand text-charcoal/60 px-2 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
